import { useEffect, useRef, useState } from 'react';

import { Box, Drawer } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Conversation, Message, Paginator } from '@twilio/conversations';
import { MessageHandler } from '@wellz/utils';
import { FormattedMessage } from 'react-intl';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import useConversations from 'hooks/useConversations';
import { pingChat } from 'services/specialist';

import { Button } from 'components/Button/Button';
import { CareMatrixModule } from 'components/CareMatrixModule';
import ChatConversationBody from 'components/ChatConversationBody';
import ChatConversationHeader from 'components/ChatConversationHeader';
import messageBalloonImage from 'images/message-balloon.png';
import newMessageImage from 'images/new-message.png';
import newMessageImage2x from 'images/new-message@2x.png';

import ChatFooter from './components/ChatFooter';

import { ChatConversationProps } from './types';

import * as S from './styles';

const ChatConversation: React.FC<ChatConversationProps> = ({
  noActiveUsers,
  activeChatUser,
  patientsListMode,
  onClickNewMessage,
  closeConversation,
  ...props
}) => {
  const { isModuleOpen } = useCareMatrix();
  const isMobile = window.innerWidth < SCREEN_MIN_WIDTH;
  const messageHandler = new MessageHandler(activeChatUser?.keys || []);

  const [messages, setMessages] = useState<Partial<Message>[]>([]);
  const [hydratedMessages, setHydratedMessages] = useState<Partial<Message>[]>(
    [],
  );
  const [paginator, setPaginator] = useState<Paginator<Message> | null>(null);

  const isInitialMount = useRef(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = (scrollBehavior: 'auto' | 'smooth') => {
    if (bottomRef && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: scrollBehavior });
    }
  };

  const onMessageAdded = () => {
    scrollToBottom('smooth');
  };

  const {
    identity,
    loadMessages,
    hydrateMessages,
    isPatientTyping,
    selectedConversation,
  } = useConversations(
    setMessages,
    setHydratedMessages,
    activeChatUser?.patientId,
    onMessageAdded,
  );

  const [loading, setLoading] = useState(true);
  const [cssAnimationsClassName, setCssAnimationsClassName] = useState('');

  useQuery({
    queryKey: ['getChatStatus', activeChatUser?.conversationId],
    queryFn: () => pingChat(activeChatUser?.conversationId || ''),
    refetchInterval: 1500,
    refetchIntervalInBackground: true,
    enabled: !!activeChatUser?.conversationId,
  });

  const textId = `CHAT.PAGE.${
    patientsListMode ? 'PATIENTS_LIST' : 'CONVERSATION'
  }.EMPTY_STATE.DESCRIPTION`;

  const handleSetMessages = (newMessages: Message[]) => {
    setMessages([...messages, ...newMessages]);
    setHydratedMessages([...hydratedMessages, ...hydrateMessages(newMessages)]);
  };

  const initConversation = async (conversation: Conversation) => {
    if (hydrateMessages.length > 0) {
      setHydratedMessages([]);
    }
    setLoading(true);
    const result = await loadMessages(conversation);
    setPaginator(result);

    handleSetMessages(result.items);
    setLoading(false);
  };

  const fetchMore = async () => {
    if (!paginator) {
      return;
    }

    setLoading(true);
    const result = await paginator?.prevPage();
    setPaginator(result);
    handleSetMessages(result.items);
    setLoading(false);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setCssAnimationsClassName(
        patientsListMode ? 'patients-mode' : 'empty-chat',
      );
    }
  }, [patientsListMode]);

  useEffect(() => {
    if (selectedConversation) {
      initConversation(selectedConversation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  useEffect(() => {
    setMessages([]);
    setHydratedMessages([]);
    setPaginator(null);
    scrollToBottom('auto');
  }, [activeChatUser]);

  return (
    <S.ChatConversationRoot {...props}>
      {!activeChatUser?.conversationId ? (
        <S.EmptyChatContainer className={cssAnimationsClassName}>
          <img
            alt="new-message"
            src={patientsListMode ? messageBalloonImage : newMessageImage}
            srcSet={patientsListMode ? '' : `${newMessageImage2x} 2x`}
          />
          <S.EmptyChatText>
            {noActiveUsers && !patientsListMode ? (
              <FormattedMessage id="CHAT.PAGE.CONVERSATION.EMPTY_STATE.DESCRIPTION.NO_USERS" />
            ) : (
              <FormattedMessage id={textId} />
            )}
          </S.EmptyChatText>
          {!patientsListMode && (
            <Button onClick={onClickNewMessage} small>
              {noActiveUsers ? (
                <FormattedMessage id="CHAT.PAGE.CONVERSATION.EMPTY_STATE.BUTTON.NEW_CONVERSATION" />
              ) : (
                <FormattedMessage id="CHAT.PAGE.CONVERSATION.EMPTY_STATE.BUTTON.SEND_NEW_MESSAGE" />
              )}
            </Button>
          )}
        </S.EmptyChatContainer>
      ) : (
        <S.ConversationWrapper>
          <ChatConversationHeader
            fullName={activeChatUser.fullName}
            avatarColor={activeChatUser.avatarColor}
            userCognitoId={activeChatUser.endUserCognitoId}
            willBeUnlinked={!!activeChatUser.expiredAt}
            closeConversation={closeConversation}
          />
          <ChatConversationBody
            ref={bottomRef}
            loading={loading}
            cpIdentity={identity}
            onScrollNext={fetchMore}
            messages={hydratedMessages}
            patientTyping={isPatientTyping}
            messageHandler={messageHandler}
            patientIdentity={activeChatUser.patientId}
            hasMoreMessages={paginator ? paginator?.hasPrevPage : true}
            scheduledMessage={activeChatUser.scheduledMessage}
          />
          <ChatFooter
            identity={identity}
            messageHandler={messageHandler}
            selectedConversation={selectedConversation}
          />
        </S.ConversationWrapper>
      )}

      {isModuleOpen && (
        <Drawer
          open={isModuleOpen && isMobile}
          sx={{ '.MuiDrawer-paper': { backgroundColor: 'white' } }}
          PaperProps={{ sx: { width: '100%' } }}
          anchor="right"
        >
          <Box width="100%" height="100%">
            <CareMatrixModule />
          </Box>
        </Drawer>
      )}
    </S.ChatConversationRoot>
  );
};

export default ChatConversation;
